import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { CompanyBoardService } from '../../../../../../services/companies/company-board.service';

export interface ElectionDialogData {
  companyId: string;
  year: string;
  existingBoardMemberIds: string[];
}

export interface ElectionCandidate {
  name: string;
  selected: boolean;
  isCustom: boolean;
  boardMemberId?: string;
  disabled?: boolean;
}

export interface ElectionDialogResult {
  candidates: ElectionCandidate[];
}

@Component({
  selector: 'app-election-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatInputModule,
    MatFormFieldModule,
    NgxSpinnerModule
  ],
  templateUrl: './election-dialog.component.html',
  styleUrls: ['./election-dialog.component.scss']
})
export class ElectionDialogComponent implements OnInit {
  private boardService = inject(CompanyBoardService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private data = inject<ElectionDialogData>(MAT_DIALOG_DATA);

  candidates: ElectionCandidate[] = [];
  customInputs: { name: string; selected: boolean }[] = [];
  selectAll = false;

  private readonly CUSTOM_SLOTS = 5;

  constructor(
    public dialogRef: MatDialogRef<ElectionDialogComponent>
  ) {}

  ngOnInit(): void {
    // Initialize 5 empty custom input slots
    for (let i = 0; i < this.CUSTOM_SLOTS; i++) {
      this.customInputs.push({ name: '', selected: false });
    }

    this.loadBoardMembers();
  }

  onToggleSelectAll(): void {
    this.candidates.forEach(c => {
      if (!c.disabled) c.selected = this.selectAll;
    });
    this.customInputs.forEach(c => {
      if (c.name.trim()) {
        c.selected = this.selectAll;
      }
    });
  }

  onCandidateChange(): void {
    this.updateSelectAllState();
  }

  onCustomSelectionChange(slot: { name: string; selected: boolean }): void {
    if (!slot.name.trim()) {
      slot.selected = false;
    }
    this.updateSelectAllState();
  }

  onConfirm(): void {
    const selected: ElectionCandidate[] = [
      ...this.candidates.filter(c => c.selected && !c.disabled),
      ...this.customInputs
        .filter(c => c.selected && c.name.trim())
        .map(c => ({ name: c.name.trim(), selected: true, isCustom: true }))
    ];

    if (selected.length === 0) {
      this.toast.warning('Please select at least one candidate');
      return;
    }

    const result: ElectionDialogResult = { candidates: selected };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private loadBoardMembers(): void {
    this.spinnerService.show();
    this.boardService.getAll(this.data.companyId, parseInt(this.data.year)).subscribe({
      next: resource => {
        // Members live in context — populated regardless of envelope state.
        const members = resource.context?.boardMembers ?? [];
        this.candidates = members
          .sort((a, b) => a.directorFullName.localeCompare(b.directorFullName))
          .map(m => {
            const alreadyExists = this.data.existingBoardMemberIds.includes(m.boardMemberId);
            return {
              name: m.directorFullName,
              selected: alreadyExists,
              isCustom: false,
              boardMemberId: m.boardMemberId,
              disabled: alreadyExists
            };
          });

        this.updateSelectAllState();
        this.spinnerService.hide();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load board members');
        console.error('Board members error:', err);
      }
    });
  }

  private updateSelectAllState(): void {
    const enabledCandidates = this.candidates.filter(c => !c.disabled);
    const allBoardSelected = enabledCandidates.length > 0 && enabledCandidates.every(c => c.selected);
    const customWithNames = this.customInputs.filter(c => c.name.trim());
    const allCustomSelected = customWithNames.length === 0 || customWithNames.every(c => c.selected);
    this.selectAll = allBoardSelected && allCustomSelected;
  }
}
